import api from '@/services/Api';
import { ActionContext, Module } from 'vuex';
import { AxiosResponse } from 'axios';
import Patient from '@/types/Patient';
import Karteieintrag from '@/types/Karteieintrag';
import { Satzart } from '@/types/Satzart';
import { PatientenauswertungState, RootState } from '@/store/types';
import { DataResponse } from '@/types';

export type PatientenauswertungFilter = {
    zeitraum: [string, string];
    impfstoffe: number[];
    reiseziele: number[];
    jahrgang: [string, string];
};

export type OpenKarteiData = DataResponse<{
    patient: Partial<Patient>,
    impfungen: Partial<Karteieintrag[]>,
}>;

export type SetPatientenauswertungDialogs = {
    filter?: boolean;
};

export type SetPatientenauswertungLoaders = {
    patienten?: boolean;
};

export type SetPatientenauswertungKarteiLoaders = {
    loadingId?: number | null;
    impfungen?: boolean;
};

type Context = ActionContext<PatientenauswertungState, RootState>;

export const patientenauswertung: Module<PatientenauswertungState, RootState> = {
    namespaced: true,
    state: {
        dialogs: {
            filter: false,
        },
        loaders: {
            patienten: false,
            kartei: {
                loadingId: null,
                impfungen: false,
            },
        },
        patienten: [],
        kartei: {
            patient: new Patient(),
            impfungen: [],
            filter: {
                satzarten: Satzart.values(),
            },
        },
    },
    actions: {
        loadPatienten({ commit }: Context, payload: { filter: PatientenauswertungFilter }): void {
            commit('setLoaders', { patienten: true });

            api.post('statistik/patientenauswertung/load', payload)
                .then((response: AxiosResponse) => commit('setPatienten', response.data.body))
                .catch(console.error)
                .finally(() => commit('setLoaders', { patienten: false }));
        },
        openKartei({ state, commit }: Context, { patnr }: Patient): void {
            commit('setLoadersKartei', { loadingId: patnr });

            api.post('impferinnerungen/impferinnerungen/kartei', { patnr, filter: state.kartei.filter })
                .then((response: AxiosResponse) => {
                    commit('setKartei', response.data);
                    commit('setDialogs', { kartei: true });
                })
                .catch(console.error)
                .finally(() => commit('setLoadersKartei', { loadingId: null }));
        },
        loadKarteiImpfungen({ state, commit }: Context, payload: { filter: PatientenauswertungFilter }): void {
            commit('setLoadersKartei', { impfungen: true });

            api.post('impferinnerungen/impferinnerungen/kartei', { patnr: state.kartei.patient.patnr, ...payload })
                .then((response: AxiosResponse) => commit('setKarteiImpfungen', response.data.body.impfungen))
                .catch(console.error)
                .finally(() => commit('setLoadersKartei', { impfungen: false }));
        },
        printFragebogen({ commit, dispatch, rootState }: Context, payload: { frageboegen: number[] }):
            void {
            commit('setLoaders', { print: true });

            const impfstelle_id = rootState.authentication.impfstelle_id;

            api.post('ivs/fragebogen/index', { impfstelle_id, ...payload }, { responseType: 'blob' })
                .then(({ data }: AxiosResponse) => dispatch('globals/printDokument', { data }, { root: true }))
                .catch(console.error)
                .finally(() => commit('setLoaders', { print: false }));
        },
        setDialogs({ commit }: Context, payload: SetPatientenauswertungDialogs): void {
            commit('setDialogs', payload);
        },
        setLoaders({ commit }: Context, payload: SetPatientenauswertungLoaders): void {
            commit('setLoaders', payload);
        },
    },
    mutations: {
        setPatienten(state: PatientenauswertungState, payload: Partial<Patient>[]): void {
            state.patienten = payload.map((data) => new Patient(data));
        },
        setKartei(state: PatientenauswertungState, { body }: OpenKarteiData): void {
            state.kartei.patient = new Patient(body.patient);
            state.kartei.impfungen = body.impfungen.map((data) => new Karteieintrag(data));
        },
        setKarteiImpfungen(state: PatientenauswertungState, payload: Partial<Karteieintrag>[]): void {
            state.kartei.impfungen = payload.map((data) => new Karteieintrag(data));
        },
        setDialogs(state: PatientenauswertungState, payload: SetPatientenauswertungDialogs): void {
            state.dialogs = {
                ...state.dialogs,
                ...payload,
            };
        },
        setLoaders(state: PatientenauswertungState, payload: SetPatientenauswertungLoaders): void {
            state.loaders = {
                ...state.loaders,
                ...payload,
            };
        },
        setLoadersKartei(state: PatientenauswertungState, payload: SetPatientenauswertungKarteiLoaders): void {
            state.loaders.kartei = {
                ...state.loaders.kartei,
                ...payload,
            };
        },
    },
};
